import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Tooltip } from '@mui/material'
import { Add, QrCode2, ViewColumn, ViewColumnOutlined } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, PermissionGate, usePermissions } from '@components/common'
import { AssetForm } from '@components/assets'
import { toast } from 'react-toastify'
import api from '@services/api'

// Essential columns shown by default
const essentialColumns = [
  { field: 'serial_number', headerName: 'SN', sortable: true, renderCell: (v) => v || '-' },
  { field: 'name', headerName: 'Description', sortable: true },
  { field: 'asset_tag', headerName: 'Tag #', sortable: true, width: 100 },
  { field: 'category_name', headerName: 'Category', sortable: true, renderCell: (v) => v || '-' },
  { field: 'location_name', headerName: 'Location', sortable: true, renderCell: (v) => v || '-' },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => <StatusChip status={value} />,
  },
]

// Additional columns shown when expanded
const additionalColumns = [
  { field: 'model_number', headerName: 'Model', sortable: true, renderCell: (v) => v || '-' },
  { field: 'manufacturer_name', headerName: 'Manufacturer', sortable: true, renderCell: (v) => v || '-' },
  {
    field: 'purchase_cost',
    headerName: 'Unit Cost',
    sortable: true,
    renderCell: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '-'
  },
  {
    field: 'purchase_date',
    headerName: 'Purchase Date',
    sortable: true,
    renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-'
  },
  { field: 'room_number', headerName: 'Room', sortable: true, renderCell: (v) => v || '-', width: 80 },
]

const AssetsList = () => {
  const navigate = useNavigate()
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [showAllColumns, setShowAllColumns] = useState(false)
  const { canCreate } = usePermissions()

  // Memoize columns based on toggle state
  const columns = useMemo(() => {
    if (showAllColumns) {
      // Insert additional columns in logical order
      return [
        ...essentialColumns.slice(0, 4), // SN, Description, Tag, Category
        ...additionalColumns.slice(0, 2), // Model, Manufacturer
        essentialColumns[4], // Location
        additionalColumns[4], // Room
        ...additionalColumns.slice(2, 4), // Unit Cost, Purchase Date
        essentialColumns[5], // Status
      ]
    }
    return essentialColumns
  }, [showAllColumns])

  const fetchAssets = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/assets/')
      setAssets(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching assets:', error)
      toast.error('Failed to load assets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssets()
  }, [])

  const handleFormSuccess = () => {
    fetchAssets()
  }

  return (
    <Box>
      <PageHeader
        title="Assets"
        subtitle="Manage all equipment and assets"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets' },
        ]}
        actions={
          <>
            <Tooltip title={showAllColumns ? 'Show fewer columns' : 'Show all columns'}>
              <Button
                variant="outlined"
                startIcon={showAllColumns ? <ViewColumn /> : <ViewColumnOutlined />}
                onClick={() => setShowAllColumns(!showAllColumns)}
                color={showAllColumns ? 'primary' : 'inherit'}
              >
                {showAllColumns ? 'Fewer Columns' : 'All Columns'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => navigate('/assets/scan')}
            >
              Scan QR
            </Button>
            <PermissionGate permission="assets.create">
              <Button
                startIcon={<Add />}
                onClick={() => setFormOpen(true)}
              >
                Add Asset
              </Button>
            </PermissionGate>
          </>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={assets}
          loading={loading}
          onRowClick={(row) => navigate(`/assets/${row.id}`)}
          searchPlaceholder="Search assets..."
          emptyMessage="No assets found"
        />
      </Card>

      <AssetForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </Box>
  )
}

export default AssetsList
